import React from "react";
import { motion } from "framer-motion";

interface MediaItem {
  logo: string;
  link: string;
  alt: string;
}

interface TheyTalkAboutUsSectionProps {
  mediaData: MediaItem[];
}

const TheyTalkAboutUsSection: React.FC<TheyTalkAboutUsSectionProps> = ({ mediaData }) => {

  return (
    <div className="relative py-12 md:py-16 overflow-hidden bg-gradient-to-br from-amber-500/3 via-orange-500/5 to-red-500/3">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/3 left-1/4 w-80 h-80 bg-amber-400/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/3 right-1/4 w-60 h-60 bg-orange-400/5 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-8">
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true, margin: "-10%" }}
        >
          <motion.h2 
            className="text-4xl lg:text-5xl font-black text-white leading-tight mb-4"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
          >
            They Talk
            <span className="bg-gradient-to-r from-amber-400 via-orange-400 to-red-400 bg-clip-text text-transparent">
              {" "}About Us
            </span>
          </motion.h2>
          
          <motion.p 
            className="text-lg text-gray-300 max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            viewport={{ once: true }}
          >
            Featured in leading tech publications worldwide
          </motion.p>
        </motion.div>

        {/* Moving Logo Carousel */}
        <div className="relative overflow-hidden">
          <motion.div 
            className="flex space-x-16 items-center"
            animate={{ x: ["-100%", "0%"] }}
            transition={{
              duration: 25,
              repeat: Infinity,
              ease: "linear"
            }}
          >
            {/* First set of logos */}
            {mediaData.map((item, index) => (
              <motion.a
                key={`first-${index}`}
                href={item.link}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-shrink-0 group"
                whileHover={{ scale: 1.1 }}
                transition={{ duration: 0.2 }}
              >
                <div className="w-48 h-24 bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 flex items-center justify-center p-4 group-hover:bg-white/10 transition-all duration-300">
                  <img
                    src={item.logo}
                    alt={item.alt}
                    className="max-w-full max-h-full object-contain opacity-70 group-hover:opacity-100 transition-opacity duration-300"
                  />
                </div>
              </motion.a>
            ))}
            
            {/* Duplicate set for seamless loop */}
            {mediaData.map((item, index) => (
              <motion.a
                key={`second-${index}`}
                href={item.link}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-shrink-0 group"
                whileHover={{ scale: 1.1 }}
                transition={{ duration: 0.2 }}
              >
                <div className="w-48 h-24 bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 flex items-center justify-center p-4 group-hover:bg-white/10 transition-all duration-300">
                  <img
                    src={item.logo}
                    alt={item.alt}
                    className="max-w-full max-h-full object-contain opacity-70 group-hover:opacity-100 transition-opacity duration-300"
                  />
                </div>
              </motion.a>
            ))}
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default TheyTalkAboutUsSection;